import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { createSportsEventSchema } from "../../utils/seo/createSportsEventSchema";
import { getRacecourse } from "../../client/racecourses/racecourse";

type Schema = { "@type": string; [key: string]: any };

type Props = {
  items?: Schema[];
  sportsEvent?: boolean;
  race?: any;
};

// https://developers.google.com/search/docs/appearance/structured-data/search-gallery

const StructuredData = ({
  items = [],
  sportsEvent = false,
  race = null,
}: Props) => {
  const [sportsEventSchema, setSportsEventSchema] = useState<Schema | null>(
    null
  );

  useEffect(() => {
    if (!sportsEvent || !race) return;

    let cancelled = false;

    const run = async () => {
      try {
        const racecourseData = await getRacecourse(race.meeting.track.slug);
        const schema = await createSportsEventSchema(
          race,
          race.runners,
          racecourseData
        );
        if (!cancelled) setSportsEventSchema(schema);
      } catch (e) {
        if (!cancelled) setSportsEventSchema(null);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [sportsEvent, race?.id]); // key dependency

  const finalItems = [
    ...items,
    ...(sportsEventSchema ? [sportsEventSchema] : []),
  ].filter(Boolean);

  if (!finalItems.length) return null;

  const context = { "@context": "https://schema.org" };

  /**
   * If we have multiple schema on one page/component, then it's better to use a @graph schema.
   * It's preferred by search engines to understand the relationships between diff schema entities,
   * so is better for overall SEO
   *
   * @graph does work with a single item, but it's unnecessary overhead so we just output a flat
   * schema in those cases
   */

  const schema =
    finalItems.length === 1
      ? { ...context, ...finalItems[0] }
      : { ...context, "@graph": finalItems };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

export default StructuredData;
